import { useCallback, useEffect, useMemo, useState } from "react";
import { DiscountItem } from "./types";

interface UsePromoCodeComponentOptions {
  /** Discount items returned from the summary/estimate API. */
  discounts: DiscountItem[];
  onApplyPromoCode: (code: string) => void;
  onRemoveDiscount: (code: string) => void;
  /** Whether a promo-code mutation is currently in flight. */
  isApplying: boolean;
  isDisabled: boolean;
  /** Seed the text input (e.g. a promo code coming from a query param). */
  initialInputValue?: string;
}

/**
 * Strategy hook that encapsulates all promo-code orchestration logic.
 *
 * It owns:
 * - the text-input value (`inputValue` / `setInputValue`)
 * - the "currently removing" coupon state
 * - derived booleans (`hasValidDiscount`, `isInvalidState`)
 * - a render-ready list of applied discounts with pre-bound `onRemove` handlers
 *
 * Consumers only need to provide the raw discount array from the API and
 * the apply/remove callbacks; the hook returns everything the UI needs.
 *
 * @example
 * ```tsx
 * const promo = usePromoCodeComponent({
 *   discounts: summary?.discounts ?? [],
 *   onApplyPromoCode: handleApply,
 *   onRemoveDiscount: handleRemove,
 *   isApplying: mutation.isPending,
 *   isDisabled: cartEmpty,
 * });
 *
 * // Then render:
 * {!promo.hasValidDiscount && <PromoCodeField ... />}
 * {promo.appliedDiscounts.map(d => <AppliedPromoCode key={d.code} ... />)}
 * ```
 */
export function usePromoCodeComponent({
  discounts,
  onApplyPromoCode,
  onRemoveDiscount,
  isApplying,
  isDisabled,
  initialInputValue = "",
}: UsePromoCodeComponentOptions) {
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [removingCouponCode, setRemovingCouponCode] = useState<string | null>(
    null,
  );

  const hasValidDiscount = useMemo(
    () => discounts.some((d) => !d.invalid),
    [discounts],
  );

  const isInvalidState = useMemo(
    () => discounts.some((d) => d.invalid),
    [discounts],
  );

  const handleApply = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed) onApplyPromoCode(trimmed);
  }, [inputValue, onApplyPromoCode]);

  const appliedDiscounts = useMemo(
    () =>
      discounts
        .filter((d) => !d.invalid)
        .map((d) => ({
          code: d.coupon_code,
          percent_off: d.percent_off,
          amount_off: d.amount_off,
          isSticky: !!d.is_sticky,
          isRemoving: removingCouponCode === d.coupon_code,
          onRemove: d.is_sticky
            ? undefined
            : () => {
                setRemovingCouponCode(d.coupon_code);
                onRemoveDiscount(d.coupon_code);
              },
        })),
    [discounts, removingCouponCode, onRemoveDiscount],
  );

  useEffect(() => {
    if (
      removingCouponCode &&
      !discounts.some((d) => d.coupon_code === removingCouponCode)
    ) {
      setRemovingCouponCode(null);
      setInputValue("");
    }
  }, [discounts, removingCouponCode]);

  return {
    inputValue,
    setInputValue,
    handleApply,
    hasValidDiscount,
    isInvalidState,
    appliedDiscounts,
    isApplying,
    isDisabled: isDisabled || isApplying,
  };
}
